import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, Download, Filter, TrendingUp, Globe, Server, Map } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { resultsAPI } from '../../services/api';
import { getResultTypeDisplayName } from '../../utils/resultTypeNames';

const Intelligence = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch only results, no need for scans list
      const resultsRes = await resultsAPI.list(null, null, null, 200); // Reduced to 200 for faster loading
      setResults(resultsRes.data || []);
      setScans([]); // Not needed for intelligence view
    } catch (error) {
      console.error('Failed to fetch intelligence data:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const exportIntelligence = () => {
    const report = {
      generated_at: new Date().toISOString(),
      total_results: results.length,
      total_scans: scans.length,
      findings_by_type: groupedByType,
      vulnerabilities: vulnerabilities,
      results: results
    };
    
    const dataStr = JSON.stringify(report, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `shinobi-intelligence-${new Date().toISOString()}.json`;
    link.click();
  };

  // Group results by type
  const groupedByType = results.reduce((acc, result) => {
    const key = result.result_type;
    if (!acc[key]) {
      acc[key] = { count: 0, new: 0 };
    }
    acc[key].count++;
    if (result.is_new) acc[key].new++;
    return acc;
  }, {});

  // Extract vulnerabilities (from metadata, specific result types, or Shodan data)
  const vulnerabilities = results.filter(r => {
    // Direct vulnerability type
    if (r.result_type === 'vulnerability') return true;
    
    // Metadata vulnerabilities
    if (r.metadata && (r.metadata.vulnerabilities || r.metadata.vulns)) return true;
    
    // Shodan data with vulns
    if (r.result_type === 'shodan_data' && r.data) {
      try {
        const data = typeof r.data === 'string' ? JSON.parse(r.data) : r.data;
        if (data.vulns && data.vulns.length > 0) return true;
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    return false;
  });
  
  // Count total unique vulnerabilities from Shodan results
  const totalVulnCount = vulnerabilities.reduce((count, r) => {
    if (r.result_type === 'shodan_data' && r.data) {
      try {
        const data = typeof r.data === 'string' ? JSON.parse(r.data) : r.data;
        return count + (data.vulns?.length || 0);
      } catch (e) {
        return count;
      }
    }
    return count + 1;
  }, 0);

  // Filter results
  let filteredResults = results;
  if (filterType !== 'all') {
    filteredResults = filteredResults.filter(r => r.result_type === filterType);
  }

  const getTypeIcon = (type) => {
    const icons = {
      subdomain: Globe,
      ip: Server,
      port: Shield,
      vulnerability: AlertTriangle,
      technology: Server
    };
    return icons[type] || Globe;
  };

  const getTypeColor = (type) => {
    const colors = {
      subdomain: 'text-shinobi-purple',
      ip: 'text-shinobi-orange',
      port: 'text-blue-600',
      vulnerability: 'text-danger-red',
      technology: 'text-yellow-600'
    };
    return colors[type] || 'text-slate-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-shinobi-purple">Loading intelligence...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-shinobi-purple glow-purple mb-2">Intelligence</h1>
          <p className="text-slate-500">Aggregated findings and analysis across all engagements</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/intelligence/map')}
            className="btn-cyber px-6 py-3 rounded flex items-center space-x-2"
          >
            <Map className="w-5 h-5" />
            <span>Map View</span>
          </button>
          {results.length > 0 && (
            <button
              onClick={exportIntelligence}
              className="btn-cyber-outline px-6 py-3 rounded flex items-center space-x-2"
            >
              <Download className="w-5 h-5" />
              <span>Export Report</span>
            </button>
          )}
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="cyber-card rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <Shield className="w-8 h-8 text-shinobi-purple" />
            <TrendingUp className="w-5 h-5 text-shinobi-purple" />
          </div>
          <div className="text-3xl font-bold text-shinobi-purple mb-1">{results.length}</div>
          <div className="text-sm text-slate-500">Total Findings</div>
        </div>

        <div className="cyber-card rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle className="w-8 h-8 text-danger-red" />
            <TrendingUp className="w-5 h-5 text-danger-red" />
          </div>
          <div className="text-3xl font-bold text-danger-red mb-1">{totalVulnCount}</div>
          <div className="text-sm text-slate-500">Vulnerabilities</div>
        </div>

        <div className="cyber-card rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <Globe className="w-8 h-8 text-shinobi-orange" />
            <TrendingUp className="w-5 h-5 text-shinobi-orange" />
          </div>
          <div className="text-3xl font-bold text-shinobi-orange mb-1">{Object.keys(groupedByType).length}</div>
          <div className="text-sm text-slate-500">Asset Types</div>
        </div>

        <div className="cyber-card rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <Server className="w-8 h-8 text-yellow-600" />
            <TrendingUp className="w-5 h-5 text-yellow-600" />
          </div>
          <div className="text-3xl font-bold text-yellow-600 mb-1">{results.filter(r => r.is_new).length}</div>
          <div className="text-sm text-slate-500">New Findings</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4 mb-6">
        <div className="flex items-center space-x-2">
          <Filter className="w-5 h-5 text-slate-500" />
          <span className="text-sm text-slate-500">Filter by type:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilterType('all')}
            className={`px-4 py-2 rounded text-sm font-semibold transition-all ${
              filterType === 'all'
                ? 'bg-shinobi-purple text-cyber-50'
                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
          >
            All ({results.length})
          </button>
          {Object.entries(groupedByType).map(([type, data]) => (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              className={`px-4 py-2 rounded text-sm font-semibold transition-all ${
                filterType === type
                  ? 'bg-shinobi-purple text-cyber-50'
                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
            >
              {getResultTypeDisplayName(type)} ({data.count})
              {data.new > 0 && <span className="ml-1 text-xs">+{data.new}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Findings Summary by Type */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {Object.entries(groupedByType).map(([type, data]) => {
          const TypeIcon = getTypeIcon(type);
          const typeColor = getTypeColor(type);

          return (
            <div key={type} className="cyber-card rounded-lg p-6 hover:scale-105 transition-transform cursor-pointer">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <TypeIcon className={`w-6 h-6 ${typeColor}`} />
                  <h3 className="text-lg font-bold text-slate-800 uppercase">{getResultTypeDisplayName(type)}</h3>
                </div>
                {data.new > 0 && (
                  <span className="px-2 py-1 rounded text-xs font-semibold bg-shinobi-purple/10 text-shinobi-purple">
                    +{data.new} NEW
                  </span>
                )}
              </div>
              <div className="text-3xl font-bold text-slate-800 mb-1">{data.count}</div>
              <div className="text-sm text-slate-500">Total discovered</div>
            </div>
          );
        })}
      </div>

      {/* Recent Findings */}
      {filteredResults.length === 0 ? (
        <div className="cyber-card rounded-lg p-12 text-center">
          <Shield className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No Intelligence Data</h3>
          <p className="text-gray-500">Run scans to gather intelligence</p>
        </div>
      ) : (
        <div className="cyber-card rounded-lg p-6">
          <h2 className="text-xl font-bold text-shinobi-purple mb-4">
            {filterType === 'all' ? 'Recent Findings' : `${getResultTypeDisplayName(filterType)} Findings`}
            <span className="text-sm text-slate-500 ml-3">({filteredResults.length} total)</span>
          </h2>
          
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredResults.slice(0, 100).map((result) => {
              const TypeIcon = getTypeIcon(result.result_type);
              const typeColor = getTypeColor(result.result_type);

              return (
                <div
                  key={result.id}
                  className="bg-slate-50 rounded p-3 hover:bg-slate-100 transition-colors flex items-start justify-between"
                >
                  <div className="flex items-start space-x-3 flex-1">
                    <TypeIcon className={`w-5 h-5 ${typeColor} mt-0.5`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-mono text-slate-800 break-all">
                        {result.value}
                      </p>
                    </div>
                  </div>
                  {result.is_new && (
                    <span className="ml-2 px-2 py-0.5 rounded text-xs font-semibold bg-shinobi-purple/10 text-shinobi-purple flex-shrink-0">
                      NEW
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Intelligence;
